/**
 * Helpers SEO invisibili (nessun impatto grafico):
 * - setPageMeta: aggiorna title + tutti i meta tag <head> per la pagina corrente
 * - injectJsonLd/removeJsonLd: gestisce blocchi Schema.org
 */

const SITE_ORIGIN = "https://permanina.it";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/logo.png`; // fallback OG image

function _ensureMeta(selector, createFn) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = createFn();
    document.head.appendChild(el);
  }
  return el;
}

function _setMetaByName(name, content) {
  if (!content) return;
  const el = _ensureMeta(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  });
  el.setAttribute("content", content);
}

function _setMetaByProp(prop, content) {
  if (!content) return;
  const el = _ensureMeta(`meta[property="${prop}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", prop);
    return m;
  });
  el.setAttribute("content", content);
}

function _setLink(rel, href) {
  if (!href) return;
  const el = _ensureMeta(`link[rel="${rel}"]`, () => {
    const l = document.createElement("link");
    l.setAttribute("rel", rel);
    return l;
  });
  el.setAttribute("href", href);
}

/**
 * Aggiorna title + description + canonical + open graph + twitter card.
 * Ritorna una funzione di cleanup che ripristina il default della home (utile per unmount).
 */
export function setPageMeta({
  title,
  description,
  canonicalPath, // e.g. "/blog/xyz" — si aggiunge automaticamente il dominio
  ogType = "website",
  ogImage,
} = {}) {
  if (title) document.title = title;
  if (description) {
    _setMetaByName("description", description);
    _setMetaByProp("og:description", description);
    _setMetaByName("twitter:description", description);
  }
  if (title) {
    _setMetaByProp("og:title", title);
    _setMetaByName("twitter:title", title);
  }
  const url = canonicalPath ? `${SITE_ORIGIN}${canonicalPath}` : window.location.origin + window.location.pathname;
  _setLink("canonical", url);
  _setMetaByProp("og:url", url);
  _setMetaByProp("og:type", ogType);
  let img = ogImage || DEFAULT_IMAGE;
  // Assicura URL assoluto (WhatsApp/Facebook non risolvono URL relativi)
  if (img && img.startsWith("/")) img = SITE_ORIGIN + img;
  _setMetaByProp("og:image", img);
  _setMetaByName("twitter:image", img);
  _setMetaByName("twitter:card", "summary_large_image");
}

/**
 * Inserisce (o aggiorna) uno script JSON-LD nel <head> con un id univoco.
 * @param {string} id  identificatore univoco (es. "schema-school", "schema-article")
 * @param {object|Array} data  oggetto o array Schema.org
 */
export function injectJsonLd(id, data) {
  if (!data) return;
  const existing = document.getElementById(id);
  const payload = JSON.stringify(data);
  if (existing) {
    existing.textContent = payload;
    return;
  }
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = payload;
  document.head.appendChild(script);
}

export function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}
